<template>
    <!--
            Composant FacetTree
            Vue arborescente générique pour les facettes (terminologies, composants, etc.)
            Props :
                - items : Tableau des éléments racines à afficher
                - parentPrefix, childPrefix, singlePrefix : chaînes, pour générer des IDs uniques
                - itemKey : fonction(item) => chaîne, retourne la clé unique d'un élément
            Événements émis :
                - toggle-selection(item) : lorsqu'une case est cochée/décochée
                - toggle-parent(item) : lorsqu'une case parent est cochée/décochée
                - toggle-expansion(type, uuid) : lorsqu'on plie/déplie un parent
        -->
    <div class="facet-tree">
        <div v-for="item in items" :key="itemKey(item)" class="tree-item">
            <!-- Élément simple (sans enfants) -->
            <div class="item-row">
                <div class="checkbox-column">
                    <div class="custom-control custom-checkbox">
                        <!-- Case à cocher simple : cochée si sélectionné -->
                        <input type="checkbox" :id="singlePrefix + itemKey(item)" class="custom-control-input"
                            :checked="item.checked && !item.protected" :disabled="item.protected"
                            @change="$emit('toggle-selection', item)">
                        <label class="custom-control-label" :for="singlePrefix + itemKey(item)"
                            :class="{ 'disabled-label': item.protected }">
                            <span class="label-text">{{ item.label }}</span>
                            <div class="counter-lock-container">
                                <i v-if="item.protected" class="fas fa-lock lock-icon"></i>
                                <span class="counter">{{ item.docCount }} / {{ item.docTotalCount }}</span>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// FacetTree : vue arborescente générique pour la sélection de facettes
// Props :
//   - items : Tableau des éléments racines à afficher
//   - parentPrefix, childPrefix, singlePrefix : chaînes, pour générer des IDs uniques
//   - itemKey : fonction(item) => chaîne, retourne la clé unique d'un élément
// Événements émis :
//   - toggle-selection(item) : lorsqu'une case est cochée/décochée
//   - toggle-parent(item) : lorsqu'une case parent est cochée/décochée
//   - toggle-expansion(type, uuid) : lorsqu'on plie/déplie un parent
export default {
    name: "TerminologyFacetTree",
    props: {
        // Tableau des éléments racines à afficher
        items: { type: Array, required: true },
        // Préfixes pour générer des IDs uniques
        parentPrefix: { type: String, default: '' },
        childPrefix: { type: String, default: '' },
        singlePrefix: { type: String, default: '' },
        // Fonction(item): chaîne, retourne la clé unique d'un élément
        itemKey: { type: Function, required: true }
    }
};
</script>

<style lang="scss" scoped>
.facet-tree {
    position: relative;
    width: 100%;

    .tree-item {
        &:last-child {
            margin-bottom: 0;
        }
    }

    .item-row {
        display: flex;
        align-items: flex-start;
        margin-bottom: 0.5rem;
    }

    .checkbox-column {
        flex: 1;
    }
}

.custom-control {
    margin-bottom: 0px; // ANS-1869 : bypass du design system
}

.custom-control-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    cursor: pointer;
    position: relative;
    min-height: 24px;

    &:hover {
        color: var(--prime, #007bff);
    }

    &.disabled-label {
        cursor: not-allowed;
        opacity: 0.6;
        color: #6c757d;

        &:hover {
            color: #6c757d;
        }
    }

    .label-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-wrap: break-word;
        padding-right: 8px;
    }

    .lock-icon {
        font-size: 12px;
        color: #6c757d;
        flex-shrink: 0;
        margin-right: 8px;
    }

    .counter {
        flex-shrink: 0;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: normal;
        background-color: rgba(255, 255, 255, 0.9);
        padding: 2px 6px;
        border-radius: 3px;
        min-width: 30px;
        text-align: center;
        line-height: 1.2;
        white-space: nowrap;
    }
}

.counter-lock-container {
    min-width: 100px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .lock-icon {
        flex-shrink: 0;
    }

    .counter {
        margin-left: auto;
    }
}

.custom-control-input:disabled~.custom-control-label {
    cursor: not-allowed;
    opacity: 0.6;
}
</style>
